// Mirror of Shared/Calculators/PaverCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "areaLengthFt", kind: "number", default: 10, min: 0.5, max: 200 },
  { key: "areaWidthFt", kind: "number", default: 12, min: 0.5, max: 200 },
  {
    key: "paverSize",
    kind: "select",
    defaultChoice: "4x8",
    choices: [
      { value: "4x8", set: { paversPerSqft: 4.5 } },
      { value: "6x6", set: { paversPerSqft: 4.0 } },
      { value: "6x9", set: { paversPerSqft: 2.67 } },
      { value: "12x12", set: { paversPerSqft: 1.0 } },
    ],
  },
  { key: "wastePct", kind: "number", default: 10, min: 0, max: 25 },
  { key: "baseDepthIn", kind: "number", default: 4, min: 0, max: 12 },
  { key: "sandDepthIn", kind: "number", default: 1, min: 0, max: 4 },
  { key: "pricePerPaver", kind: "number", default: 0.75, min: 0, max: 20 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const l = F.get(i, "areaLengthFt");
  const w = F.get(i, "areaWidthFt");
  const perSqft = F.get(i, "paversPerSqft");
  const waste = F.get(i, "wastePct");
  const baseDepth = F.get(i, "baseDepthIn");
  const sandDepth = F.get(i, "sandDepthIn");
  const price = F.get(i, "pricePerPaver");

  const area = l * w;
  const areaWithWaste = area * (1 + waste / 100);
  const pavers = F.ceilUnits(areaWithWaste * perSqft);
  const gravelCuFt = area * (baseDepth / 12);
  const gravelCuYd = gravelCuFt / 27;
  const sandCuFt = area * (sandDepth / 12);
  const cost = pavers * price;

  return {
    area: F.round(area, 2),
    areaWithWaste: F.round(areaWithWaste, 2),
    pavers,
    gravelCuFt: F.round(gravelCuFt, 2),
    gravelCuYd: F.round(gravelCuYd, 2),
    sandCuFt: F.round(sandCuFt, 2),
    cost: F.round(cost, 2),
  };
}

function present(_i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const pavers = F.get(r, "pavers");
  return [
    { label: "Patio area", value: `${F.dec(F.get(r, "area"))} sq ft` },
    { label: "Pavers to buy", value: `${F.intStr(pavers)} ${F.plural(pavers, "paver", "pavers")}`, highlight: true },
    { label: "Gravel base", value: `${F.dec(F.get(r, "gravelCuYd"))} cu yd` },
    { label: "Bedding sand", value: `${F.dec(F.get(r, "sandCuFt"))} cu ft` },
    { label: "Est. paver cost", value: F.usd(F.get(r, "cost")), sub: "rough — pavers only, excludes base, sand, edging & labor" },
  ];
}

export const paver: CalcDef = { slug: "paver-calculator", fields, compute, present };
